import { HelperMethods } from '_utils';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import React, { useRef } from 'react';
import Barcode from 'react-barcode';
import {
  MdCheckCircle,
  MdDownload,
  MdEvent,
  MdLocationOn,
  MdPeople,
  MdPhotoCamera,
  MdPrint,
} from 'react-icons/md';
import main_logo from '../../assets/logos/main_logo.png';

import { BookingData } from '@types';
import { excludeFromTicketCapture, SuccessAppDownloadBanner } from '_atoms';

interface TicketConfirmationProps {
  bookingData: BookingData;
  ticketNumber: string;
}

export const TicketConfirmation: React.FC<TicketConfirmationProps> = ({
  bookingData,
  ticketNumber,
}) => {
  const ticketElementRef = useRef<HTMLDivElement>(null);

  const calculateTotal = () => {
    // Use actual amount paid from paymentInfo if available (amount is in cents)
    if (bookingData.paymentInfo?.amount !== undefined) {
      return bookingData.paymentInfo.amount / 100;
    }

    // Fallback to calculating original total if paymentInfo not available
    if (!bookingData.selectedTimeSlot || !bookingData.skatersInfo) return 0;

    const adultCount = bookingData.skatersInfo.filter(
      skater => skater.isAdult,
    ).length;
    const childCount = bookingData.skatersInfo.filter(
      skater => skater.isChild,
    ).length;

    const total =
      adultCount * bookingData.selectedTimeSlot.adultPrice +
      childCount * bookingData.selectedTimeSlot.childPrice;

    return total;
  };

  const calculateOriginalTotal = () => {
    if (!bookingData.selectedTimeSlot || !bookingData.skatersInfo) return 0;

    const adultCount = bookingData.skatersInfo.filter(
      skater => skater.isAdult,
    ).length;
    const childCount = bookingData.skatersInfo.filter(
      skater => skater.isChild,
    ).length;

    const total =
      adultCount * bookingData.selectedTimeSlot.adultPrice +
      childCount * bookingData.selectedTimeSlot.childPrice;

    return total;
  };

  const getAppliedCoupons = (): any[] => {
    // Handle both array and single coupon (for backward compatibility)
    if (!bookingData.couponAttached) return [];
    if (Array.isArray(bookingData.couponAttached)) {
      return bookingData.couponAttached;
    }
    return [bookingData.couponAttached];
  };

  const generateTicketPDF = async (): Promise<{
    pdf: jsPDF;
    filename: string;
  }> => {
    if (!ticketElementRef.current) {
      throw new Error('Ticket element not found');
    }

    // Convert the ticket element to an image
    const png = await toPng(ticketElementRef.current, {
      quality: 1.0,
      backgroundColor: '#ffffff',
      filter: excludeFromTicketCapture,
    });

    // Create a new jsPDF instance
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;

    // Create an image element to get dimensions
    const img = new Image();
    img.src = png;

    return new Promise((resolve, reject) => {
      img.onload = () => {
        try {
          // Calculate dimensions to fit the image on the page
          const imgWidth = pageWidth - margin * 2;
          const imgHeight = (img.height * imgWidth) / img.width;

          // If image is too tall, scale it down
          let finalWidth = imgWidth;
          let finalHeight = imgHeight;

          if (imgHeight > pageHeight - margin * 2) {
            finalHeight = pageHeight - margin * 2;
            finalWidth = (img.width * finalHeight) / img.height;
          }

          // Center the image on the page
          const x = (pageWidth - finalWidth) / 2;
          const y = (pageHeight - finalHeight) / 2;

          // Add the image to the PDF
          pdf.addImage(png, 'PNG', x, y, finalWidth, finalHeight);

          // Generate filename using session date and time
          const sessionDate = new Date(
            bookingData.selectedTimeSlot?.date || '',
          );
          const dateStr = sessionDate.toISOString().split('T')[0]; // YYYY-MM-DD
          const timeStr = sessionDate
            .toTimeString()
            .split(' ')[0]
            .replace(/:/g, ''); // HHMMSS
          const filename = `MOI_Ticket_${dateStr}_${timeStr}.pdf`;

          resolve({ pdf, filename });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load ticket image'));
      };
    });
  };

  const onPrint = async () => {
    try {
      const { pdf, filename } = await generateTicketPDF();

      // Create a blob URL for the PDF
      const pdfBlob = pdf.output('blob');
      const blobUrl = URL.createObjectURL(pdfBlob);

      // Create a hidden iframe to print the PDF
      const printFrame = document.createElement('iframe');
      printFrame.style.display = 'none';
      printFrame.src = blobUrl;

      document.body.appendChild(printFrame);

      printFrame.onload = () => {
        try {
          // Add a small delay to ensure the PDF is fully loaded
          setTimeout(() => {
            if (printFrame.contentWindow) {
              printFrame.contentWindow.print();

              // Listen for afterprint event to clean up
              printFrame.contentWindow.onafterprint = () => {
                setTimeout(() => {
                  if (document.body.contains(printFrame)) {
                    document.body.removeChild(printFrame);
                  }
                  URL.revokeObjectURL(blobUrl);
                }, 1000);
              };
            }
          }, 500);
        } catch (error) {
          console.error('Error printing PDF:', error);
          // Fallback: download the PDF if printing fails
          pdf.save(filename);
          // Clean up
          if (document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
          URL.revokeObjectURL(blobUrl);
        }
      };

      // Fallback cleanup in case onload doesn't fire
      setTimeout(() => {
        if (document.body.contains(printFrame)) {
          document.body.removeChild(printFrame);
        }
        URL.revokeObjectURL(blobUrl);
      }, 10000); // 10 second timeout
    } catch (error) {
      console.error('Error generating PDF for printing:', error);
      alert('Error generating PDF for printing. Please try again.');
    }
  };

  const onDownload = async () => {
    try {
      const { pdf, filename } = await generateTicketPDF();
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const onSavePhoto = async () => {
    try {
      if (!ticketElementRef.current) {
        throw new Error('Ticket element not found');
      }

      // Convert the ticket element to a PNG image
      const dataUrl = await toPng(ticketElementRef.current, {
        quality: 1.0,
        backgroundColor: '#ffffff',
        pixelRatio: 2, // Higher resolution for better quality
        filter: excludeFromTicketCapture,
      });

      // Create a download link
      const sessionDate = new Date(bookingData.selectedTimeSlot?.date || '');
      const dateStr = sessionDate.toISOString().split('T')[0]; // YYYY-MM-DD
      const link = document.createElement('a');
      link.download = `MOI_Ticket_${dateStr}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error saving photo:', error);
      alert('Error saving photo. Please try again.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Ticket Content */}
      <div ref={ticketElementRef} className="space-y-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src={main_logo} alt="MOI Logo" className="h-12 w-auto" />
              <div>
                <h1 className="text-2xl font-bold">Skating Session Ticket</h1>
                <p className="text-blue-100">
                  Your skating session has been successfully booked!
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <MdCheckCircle className="text-green-400 w-6 h-6" />
                <span className="font-semibold">Confirmed</span>
              </div>
              <p className="text-sm text-blue-100">
                {HelperMethods.formatDateTimeLocal(new Date().toISOString())}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Ticket Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <MdEvent className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">Date & Time</p>
                  <p className="font-medium text-gray-900">
                    {bookingData?.selectedTimeSlot?.date
                      ? HelperMethods.formatDate(
                          bookingData.selectedTimeSlot.date,
                        )
                      : ''}
                  </p>
                  <p className="font-medium text-gray-900">
                    {bookingData?.selectedTimeSlot?.timeSlot
                      ? HelperMethods.formatTime12Hour(
                          bookingData.selectedTimeSlot.timeSlot,
                        )
                      : ''}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MdPeople className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">Number of Skaters</p>
                  <p className="font-medium text-gray-900">
                    {bookingData.skatersInfo?.length || 0} skaters
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MdLocationOn className="text-blue-600 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-600">Location</p>
                  <a
                    href="https://www.google.com/maps/dir/Modesto+on+Ice,+11th+St,+Modesto,+CA+95354"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer"
                  >
                    1131 11th Street • Modesto, CA 95354
                  </a>
                </div>
              </div>
            </div>

            {/* Barcode and QR Code */}
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Barcode
                </p>
                <div className="bg-white border border-gray-300 p-3 rounded">
                  <div className="font-mono text-xs leading-tight text-center flex items-center justify-center">
                    <Barcode value={ticketNumber} format="CODE128" />
                  </div>
                  <p className="text-xs text-center text-gray-600 mt-1">
                    {ticketNumber}
                  </p>
                </div>
              </div>

              {/* <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  QR Code
                </p>
                <div className="bg-white border border-gray-300 p-3 rounded">
                  <div className="font-mono text-xs leading-tight text-center">
                    <QRCode value={ticketNumber} />
                  </div>
                </div>
              </div> */}
            </div>
          </div>

          {/* Skater Details */}
          {bookingData.skatersInfo && bookingData.skatersInfo.length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Skater Details</h4>
              <div className="space-y-2">
                {bookingData.skatersInfo.map((skater: any, index: number) => {
                  const waiver = bookingData.skaterWaivers?.find(
                    (w: any) =>
                      w.skaterName === `${skater.firstName} ${skater.lastName}`,
                  );
                  return (
                    <div
                      key={skater.id}
                      className="flex justify-between items-center bg-gray-50 p-3 rounded"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {skater.firstName} {skater.lastName}
                        </p>
                        <p className="text-sm text-gray-600">
                          Age:{' '}
                          {skater.dateOfBirth
                            ? HelperMethods.calculateAge(skater.dateOfBirth)
                            : 'N/A'}{' '}
                        </p>
                        {waiver && (
                          <p className="text-xs text-gray-500 mt-1">
                            Waiver signed by: {waiver.signedBy} (
                            {waiver.relationship})
                          </p>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        Skater #{index + 1}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Important Information */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-900 mb-3">
              Important Information
            </h4>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <ul className="text-sm text-blue-800 space-y-1">
                <li>
                  • Please arrive 15 minutes before your scheduled session
                </li>
                <li>
                  • Present this ticket or QR code at Ticket Booth check-in
                </li>
                <li>
                  • Skate rentals are included with your ticket, or you can
                  bring your own skates
                </li>
                <li>• Helmets are recommended for beginners</li>
                <li>• No refunds will be issued for missed sessions</li>
                <li>
                  • A confirmation email has been sent to your email address
                </li>
              </ul>
            </div>
          </div>

          {/* Applied Coupons */}
          {getAppliedCoupons().length > 0 && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">
                Applied Coupons
              </h4>
              <div className="space-y-2">
                {getAppliedCoupons().map((coupon: any, index: number) => (
                  <div
                    key={coupon.id || index}
                    className="bg-green-50 border border-green-200 rounded-md p-3"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-green-900">
                          {coupon.couponCode}
                        </p>
                        <p className="text-sm text-green-700">
                          {coupon.discountType === 'Percentage'
                            ? `${coupon.discountValue}% off`
                            : `$${coupon.discountValue} off`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pricing Breakdown */}
          {(calculateOriginalTotal() > 0 || calculateTotal() > 0) && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">
                Pricing Summary
              </h4>
              <div className="space-y-2">
                {calculateOriginalTotal() > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Original Total:</span>
                    <span className="font-medium">
                      ${calculateOriginalTotal().toFixed(2)}
                    </span>
                  </div>
                )}
                {calculateOriginalTotal() > calculateTotal() && (
                  <div className="flex justify-between text-green-600">
                    <span className="font-medium">Discount Applied:</span>
                    <span className="font-medium">
                      -$
                      {(calculateOriginalTotal() - calculateTotal()).toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">
                      Amount Paid:
                    </span>
                    <span className="text-2xl font-bold text-blue-600">
                      {calculateTotal() === 0 ? (
                        <span className="text-green-600">
                          $0.00 (Covered by passes)
                        </span>
                      ) : (
                        `$${calculateTotal().toFixed(2)}`
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <SuccessAppDownloadBanner />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <button
            onClick={onPrint}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdPrint className="w-4 h-4 mr-2" />
            Print
          </button>
          <button
            onClick={onSavePhoto}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <MdPhotoCamera className="w-4 h-4 mr-2" />
            Save Photo
          </button>
          <button
            onClick={onDownload}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MdDownload className="w-4 h-4 mr-2" />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};
